const setObservation = ({
  model, datasetId, networkId, nodeId, value, variableName, distribution,
}) => {
  const datasetIndex = model.dataSets.findIndex((dataset) => dataset.id === datasetId);
  if (datasetIndex < 0) {
    console.log('No such dataset');
    return undefined;
  }
  const dataset = model.dataSets[datasetIndex];
  dataset.observations = dataset.observations || [];
  const observationIndex = dataset.observations.findIndex((observation) => observation.network === networkId && observation.node === nodeId && observation.constantName === variableName);

  let observationValue = value;
  if (typeof value === 'string') {
    observationValue = `${value}`.trim();
  }

  const observation = {
    network: networkId,
    node: nodeId,
  };

  if (variableName) {
    observation.constantName = variableName;
  }

  let observationDistribution = distribution;
  if (typeof distribution !== 'object') {
    observationDistribution = undefined;
  } else {
    observationDistribution = Object.fromEntries(Object.entries(observationDistribution).filter((entry) => Number.parseFloat(entry[1]) > 0));
    if (Object.keys(observationDistribution).length === 0) {
      observationDistribution = undefined;
    }
  }

  if (observationDistribution && variableName) {
    // Observation as ditribution is invalid if this is a node variable observation
    observationDistribution = undefined;
  }

  if (observationValue && observationDistribution) {
    // Having both observationValue and observationDistribution is invalid, assume observationDistribution
    observationValue = undefined;
  }

  if (!observationValue && !observationDistribution) {
    // Empty value
    if (observationIndex >= 0) {
      // Remove existing
      console.log('Clear obs', { ...observation, dataset: datasetId, ...variableName && { constantName: variableName } });
      dataset.observations.splice(observationIndex, 1);
    }
    // Skip
    return observation;
  }

  if (observationValue) {
    observation.entries = [
      {
        weight: 1,
        value: observationValue,
      },
    ];
  } else {
    observation.entries = Object.keys(observationDistribution).map((state) => ({
      value: state,
      weight: observationDistribution[state],
    }));
  }

  console.log('Set obs', observation);

  if (observationIndex < 0) {
    dataset.observations.push(observation);
  } else {
    dataset.observations[observationIndex] = observation;
  }

  return observation;
};

const getObservation = ({
  model, datasetId, networkId, nodeId, variableName,
}) => {
  const datasetIndex = model.dataSets.findIndex((dataset) => dataset.id === datasetId);
  if (datasetIndex < 0) {
    console.log('No such dataset');
    return null;
  }
  const dataset = model.dataSets[datasetIndex];
  if (!dataset.observations) {
    return null;
  }

  const observationIndex = dataset.observations.findIndex((el) => el.network === networkId && el.node === nodeId && variableName === el.constantName);

  if (observationIndex < 0) {
    return null;
  }

  return dataset.observations[observationIndex];
};

const getResult = ({
  model, datasetId, networkId, nodeId,
}) => {
  const datasetIndex = model.dataSets.findIndex((dataset) => dataset.id === datasetId);
  if (datasetIndex < 0) {
    return null;
  }
  const dataset = model.dataSets[datasetIndex];
  const resultIndex = dataset.results.findIndex((el) => el.network === networkId && el.node === nodeId);

  if (resultIndex < 0) {
    return null;
  }

  return dataset.results[resultIndex];
};

const getNetwork = ({ model, networkId }) => {
  if (!model || !model.networks) {
    return null;
  }
  return model.networks.find((network) => network.id === networkId) || null;
};

const getNode = ({ network, nodeId }) => {
  if (!network || !network.nodes) {
    return null;
  }
  return network.nodes.find((node) => node.id === nodeId) || null;
};

const getDataset = ({ model, datasetId }) => {
  if (!model || !model.dataSets) {
    return null;
  }
  return model.dataSets.find((dataset) => dataset.id === datasetId) || null;
};

const validate = (modelJson) => {
  const messages = [];
  const model = modelJson?.model || modelJson || {};
  const networks = Array.isArray(model.networks) ? model.networks : [];
  const dataSets = Array.isArray(model.dataSets) ? model.dataSets : [];

  const addMessage = (message) => {
    if (message) {
      messages.push(message);
    }
  };

  const formatNodeRef = (networkId, nodeId) => `${networkId}.${nodeId}`;

  const isNumeric = (value) => {
    if (typeof value === 'number') {
      return Number.isFinite(value);
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return false;
      }
      return Number.isFinite(Number.parseFloat(trimmed));
    }
    return false;
  };

  const parseIntervalLabel = (label) => {
    if (typeof label !== 'string') {
      return null;
    }
    const match = label.match(
      /^\s*([+-]?(?:\d*\.?\d+(?:e[+-]?\d+)?|Infinity))\s*-\s*([+-]?(?:\d*\.?\d+(?:e[+-]?\d+)?|Infinity))\s*$/i,
    );
    if (!match) {
      return null;
    }
    const parseBound = (token) => {
      const normalized = token.trim().toLowerCase();
      if (normalized === 'infinity' || normalized === '+infinity') {
        return Infinity;
      }
      if (normalized === '-infinity') {
        return -Infinity;
      }
      return Number.parseFloat(token);
    };
    const lower = parseBound(match[1]);
    const upper = parseBound(match[2]);
    if (!Number.isFinite(lower) && lower !== Infinity && lower !== -Infinity) {
      return null;
    }
    if (!Number.isFinite(upper) && upper !== Infinity && upper !== -Infinity) {
      return null;
    }
    return { lower, upper };
  };

  const isIntervalNode = (node) => {
    const nodeType = node?.configuration?.type;
    return nodeType === 'IntegerInterval' || nodeType === 'ContinuousInterval';
  };

  const allowedTopLevelFunctions = new Set([
    'Arithmetic',
    'Beta',
    'BetaPert',
    'Binomial',
    'ChiSquared',
    'Comparative',
    'Exponential',
    'ExtremeValue',
    'Gamma',
    'Geometric',
    'Hypergeometric',
    'LogNormal',
    'Logistic',
    'NegativeBinomial',
    'Normal',
    'Poisson',
    'Student',
    'TNormal',
    'Triangle',
    'Uniform',
    'Weibull',
  ]);
  const isAllowedFunctionName = (name) => {
    if (!name) {
      return false;
    }
    return Array.from(allowedTopLevelFunctions).some((prefix) => name.startsWith(prefix));
  };


  const validateExpression = (expression) => {
    if (typeof expression !== 'string' || !expression.trim()) {
      return { valid: false, errorType: 'parse' };
    }
    if (!isAllowedFunctionName(expression)) {
      return { valid: false, errorType: 'tokens' };
    }
    return { valid: true };
  };

  const networkLookup = new Map();
  networks.forEach((network) => {
    const nodeMap = new Map();
    (network?.nodes || []).forEach((node) => {
      nodeMap.set(node.id, node);
    });
    const parentsMap = new Map();
    (network?.links || []).forEach((link) => {
      if (!link?.child) {
        return;
      }
      const parentList = parentsMap.get(link.child) || [];
      if (link.parent) {
        parentList.push(link.parent);
      }
      parentsMap.set(link.child, parentList);
    });
    networkLookup.set(network?.id, { network, nodeMap, parentsMap });
  });

  const getNetworkById = (networkId) => networkLookup.get(networkId)?.network || null;
  const getNodeById = (networkId, nodeId) => networkLookup.get(networkId)?.nodeMap.get(nodeId) || null;
  const getParentIds = (networkId, nodeId) => networkLookup.get(networkId)?.parentsMap.get(nodeId) || [];

  const getExpectedTableShape = (networkId, node) => {
    const states = Array.isArray(node?.configuration?.states) ? node.configuration.states : [];
    if (states.length === 0) {
      return { rows: 0, cols: 0 };
    }
    const parentIds = getParentIds(networkId, node.id);
    let columns = 1;
    parentIds.forEach((parentId) => {
      const parentNode = getNodeById(networkId, parentId);
      const parentStates = Array.isArray(parentNode?.configuration?.states)
        ? parentNode.configuration.states
        : [];
      columns *= parentStates.length || 0;
    });
    return { rows: states.length, cols: columns };
  };

  const buildObservationMessage = (networkId, nodeId, reason) => {
    let message = 'Failed loading observation';
    if (nodeId) {
      message = `Failed loading observation for node ${nodeId}`;
      if (networkId) {
        message = `Failed loading observation for node ${nodeId} in network ${networkId}`;
      }
    }
    return `${message}: ${reason}`;
  };

  const formatObservationFailure = (networkId, nodeId, reason) => {
    let message = reason;
    if (nodeId) {
      message = `${nodeId}`;
      if (networkId) {
        message = `${nodeId} in network ${networkId}`;
      }
      message = `${message}: ${reason}`;
    }
    return message;
  };

  const buildResultMessage = (networkId, nodeId) => (
    `Not found calculation data for node with ID ${nodeId} in network with ID ${networkId}`
  );

  const observationFailures = [];

  dataSets.forEach((dataset) => {
    const observations = Array.isArray(dataset?.observations) ? dataset.observations : [];
    observations.forEach((observation) => {
      const networkId = observation?.network;
      const nodeId = observation?.node;
      const network = getNetworkById(networkId);
      const node = getNodeById(networkId, nodeId);
      if (!network || !node) {
        observationFailures.push({ networkId, nodeId, reason: 'node/network not found' });
        return;
      }
      const entries = Array.isArray(observation?.entries) ? observation.entries : [];
      if (entries.length === 0) {
        observationFailures.push({ networkId, nodeId, reason: 'empty observation not allowed' });
        return;
      }
      const intervalNode = isIntervalNode(node);
      const states = Array.isArray(node.configuration?.states) ? node.configuration.states : [];
      entries.forEach((entry) => {
        const entryValue = entry?.value;
        if (entryValue === undefined || entryValue === null || entryValue === '') {
          observationFailures.push({ networkId, nodeId, reason: 'empty observation value not allowed' });
          return;
        }
        if (intervalNode) {
          if (!isNumeric(entryValue)) {
            observationFailures.push({
              networkId,
              nodeId,
              reason: `'${entryValue}' is not a number`,
            });
          }
        } else if (states.length > 0 && !states.includes(entryValue)) {
          observationFailures.push({
            networkId,
            nodeId,
            reason: `'${entryValue}' is not a valid node state`,
          });
        }
      });
    });

    const results = Array.isArray(dataset?.results) ? dataset.results : [];
    results.forEach((result) => {
      const networkId = result?.network;
      const nodeId = result?.node;
      const network = getNetworkById(networkId);
      const node = getNodeById(networkId, nodeId);
      if (!network || !node) {
        addMessage(buildResultMessage(networkId, nodeId));
        return;
      }

      const resultValues = Array.isArray(result?.resultValues) ? result.resultValues : [];
      const states = Array.isArray(node.configuration?.states) ? node.configuration.states : [];
      const isSimulated = node.configuration?.simulated === true;
      if (!isSimulated && states.length > 0 && resultValues.length !== states.length) {
        addMessage(`Recalculation required: result data corrupted or missing for node ${formatNodeRef(networkId, nodeId)} (1)`);
        return;
      }

      if (resultValues.length > 0) {
        if (isIntervalNode(node)) {
          let previousUpper = null;
          let invalidIntervals = false;
          for (let i = 0; i < resultValues.length; i += 1) {
            const label = resultValues[i]?.label;
            const parsed = parseIntervalLabel(label);
            if (!parsed || parsed.lower > parsed.upper) {
              invalidIntervals = true;
              break;
            }
            if (previousUpper !== null && previousUpper > parsed.lower) {
              invalidIntervals = true;
              break;
            }
            previousUpper = parsed.upper;
          }
          if (invalidIntervals) {
            addMessage(`Recalculation required: result data corrupted or missing for node ${formatNodeRef(networkId, nodeId)} (2)`);
          }
        } else if (states.length > 0) {
          const invalidLabel = resultValues.some((entry) => !states.includes(entry?.label));
          if (invalidLabel) {
            addMessage(`Recalculation required: result data corrupted or missing for node ${formatNodeRef(networkId, nodeId)} (3)`);
          }
        }
      }
    });
  });

  if (observationFailures.length === 1) {
    const failure = observationFailures[0];
    addMessage(buildObservationMessage(failure.networkId, failure.nodeId, failure.reason));
  } else if (observationFailures.length > 1) {
    addMessage('Failed loading observations for nodes:');
    observationFailures.forEach((failure) => {
      addMessage(`• ${formatObservationFailure(failure.networkId, failure.nodeId, failure.reason)}`);
    });
  }

  networks.forEach((network) => {
    const networkId = network?.id;
    const nodes = Array.isArray(network?.nodes) ? network.nodes : [];
    const links = Array.isArray(network?.links) ? network.links : [];

    links.forEach((link) => {
      const parentId = link?.parent;
      const childId = link?.child;
      if (!parentId || !childId) {
        addMessage(`Ignored link from ${formatNodeRef(networkId, parentId)} to ${formatNodeRef(networkId, childId)}: missing node id`);
        return;
      }
      const parentNode = getNodeById(networkId, parentId);
      const childNode = getNodeById(networkId, childId);
      if (!parentNode || !childNode) {
        const reason = !parentNode ? 'parent node not found' : 'child node not found';
        addMessage(`Ignored link from ${formatNodeRef(networkId, parentId)} to ${formatNodeRef(networkId, childId)}: ${reason}`);
      }
    });

    nodes.forEach((node) => {
      const nodeId = node?.id;
      const nodeRef = formatNodeRef(networkId, nodeId);
      const table = node?.configuration?.table;
      const tableType = table?.type;
      const states = Array.isArray(node?.configuration?.states) ? node.configuration.states : [];
      const isSimulated = node?.configuration?.simulated === true;

      if (tableType && !['Manual', 'Expression', 'Partitioned'].includes(tableType)) {
        addMessage(`Failed to load table for node ${nodeRef}: invalid table type`);
      }

      if (table?.probabilities && Array.isArray(table.probabilities)) {
        const expected = getExpectedTableShape(networkId, node);
        const rowMatch = table.probabilities.length === expected.rows;
        const colMatch = table.probabilities.every((row) => Array.isArray(row) && row.length === expected.cols);
        if (tableType === 'Manual' && (!rowMatch || !colMatch)) {
          addMessage(`Failed to load table for node ${nodeRef}: table shape mismatch`);
        }
        // if (!rowMatch || !colMatch) {
        //   addMessage(`Failed to load table for node ${nodeRef}: table shape mismatch`);
        //   if (tableType !== 'Manual') {
        //     addMessage(`Node ${nodeRef} underlying table was corrupted and will need to be recalculated`);
        //   }
        // }
      }

      if (tableType === 'Manual' && isSimulated) {
        addMessage(`Node ${nodeRef} is simulated, but the table data had a Manual table type. We recommend to check the expressions in this node.`);
      }

      if (tableType === 'Expression' || tableType === 'Partitioned') {
        const variables = Array.isArray(node?.configuration?.variables) ? node.configuration.variables : [];
        const variableNames = variables.map((variable) => variable?.name).filter(Boolean);
        const parentIds = getParentIds(networkId, nodeId);
        const allowedIdentifiers = new Set([...parentIds, ...variableNames]);
        const expressions = Array.isArray(table?.expressions) ? table.expressions : [];
        let hasExpressionError = false;
        let hasTokenError = false;
        let hasParseError = false;

        if (expressions.length === 0) {
          hasExpressionError = true;
          hasParseError = true;
        } else {
          expressions.forEach((expression) => {
            const validation = validateExpression(expression, allowedIdentifiers);
            if (!validation.valid) {
              hasExpressionError = true;
              if (validation.errorType === 'tokens') {
                hasTokenError = true;
              } else {
                hasParseError = true;
              }
            }
          });
        }

        if (hasTokenError) {
          addMessage(`Functions for node ${nodeRef} contain invalid tokens. We recommend to check the expressions in this node.`);
        }
        if (hasParseError) {
          addMessage(`Failed parsing expressions for node ${nodeRef}`);
        }
        if (hasExpressionError) {
          addMessage(`Failed to load table for node ${nodeRef}: invalid expressions`);
        }
      }

      if (tableType === 'Partitioned') {
        const partitions = table?.partitions;
        const expressions = table?.expressions;
        const partitionsValid = Array.isArray(partitions) && partitions.length > 0;
        const expressionsValid = Array.isArray(expressions) && expressions.length > 0;
        let invalidPartitionedDefinition = !partitionsValid || !expressionsValid;
        if (!invalidPartitionedDefinition && expressionsValid) {
          const variables = Array.isArray(node?.configuration?.variables) ? node.configuration.variables : [];
          const variableNames = variables.map((variable) => variable?.name).filter(Boolean);
          const parentIds = getParentIds(networkId, nodeId);
          const allowedIdentifiers = new Set([...parentIds, ...variableNames]);
          invalidPartitionedDefinition = expressions.some(
            (expression) => !validateExpression(expression, allowedIdentifiers).valid,
          );
        }
        if (invalidPartitionedDefinition) {
          addMessage(`Partitioned table definition missing or corrupt in node ${nodeRef}: please check the expressions in this node`);
        }
      }

      if (isIntervalNode(node) && !isSimulated && states.length > 0) {
        let invalidIntervals = false;
        let previousUpper = null;
        for (let i = 0; i < states.length; i += 1) {
          const parsed = parseIntervalLabel(states[i]);
          if (!parsed || parsed.lower > parsed.upper) {
            invalidIntervals = true;
            break;
          }
          if (previousUpper !== null && previousUpper > parsed.lower) {
            invalidIntervals = true;
            break;
          }
          previousUpper = parsed.upper;
        }
        if (invalidIntervals) {
          addMessage(`Non-consecutive state intervals detected in ${nodeRef}.`);
        }
      }
    });
  });

  return {
    acceptable: true, // messages.length === 0,
    messages,
  };
};

export default {
  setObservation,
  getObservation,
  getResult,
  getNetwork,
  getNode,
  getDataset,
  validate,
};
